import { getSyncClient } from '../features/sync/client';
import { syncEngine } from '../features/sync/engine';
import { clearOutbox } from '../features/sync/outbox';
import { SyncAuthError } from '../features/sync/types';
import type { SyncedTree } from '../lib/types';
import { isValidPbId } from '../lib/uid';
import { hydrateDeviceStore, useDeviceStore } from '../store/device';
import { hydrateSyncedStore, useSyncedStore } from '../store/synced';
import { buildDefaultFamily } from './bootstrap';

// One-time app boot: pull persisted state into both stores, then seed the
// synced tree if this is a first-launch device. Call from main.tsx before
// rendering routes that depend on tree contents.
//
// Sync wiring per plan §8: if the device already has a sync code, kick
// off the engine in the background. We never block boot on the network;
// the UI continues to read from local state until the engine catches up.
// Boot-time auth failure (token expired, family removed) clears the
// device's syncCode and sets `syncAuthLost` so the Sync settings page
// can render a banner on the next render.
export async function bootApp(): Promise<void> {
  hydrateDeviceStore();
  await hydrateSyncedStore();
  const hydratedTree = useSyncedStore.getState().tree;
  if (hydratedTree === null) {
    useSyncedStore.getState().setTree(buildDefaultFamily());
  } else if (treeHasLegacyIds(hydratedTree)) {
    // Pre-fix builds generated 36-char UUIDs that PocketBase rejects on
    // upsert (see #12). Wipe local state on first boot of the fixed
    // build: tree replaced via buildDefaultFamily(); device sync code
    // cleared since any "connected" state was a lie; PB JWT cleared so
    // isAuthed() doesn't lie either; outbox cleared so the queue
    // doesn't keep retrying the old UUID writes (see #15). One-shot —
    // once ids are 15 chars the predicate returns false.
    console.info('[sync:boot] reset legacy UUID tree');
    useSyncedStore.getState().setTree(buildDefaultFamily());
    useDeviceStore.getState().setSyncCode(null);
    const client = await getSyncClient();
    client.authClear();
    await clearOutbox();
  }

  const syncCode = useDeviceStore.getState().syncCode;
  if (syncCode) {
    // Fire-and-forget so we don't block boot on the network. Tests can
    // observe the result via `bootApp.pendingSyncStart` (set below).
    bootApp.pendingSyncStart = syncEngine
      .start({ syncCode })
      .then(() => {
        console.info('[sync:boot] start finished status=running');
      })
      .catch((e) => {
        if (e instanceof SyncAuthError) {
          useDeviceStore.getState().setSyncCode(null);
          useDeviceStore.getState().setSyncAuthLost(true);
          console.info('[sync:boot] start finished status=auth-failed');
          return;
        }
        // Network errors / unknown failures: leave the code in place so a
        // later retry (next boot or Sync page) can succeed.
        console.warn('[sync:boot] syncEngine.start failed', e);
      });
  } else {
    bootApp.pendingSyncStart = null;
  }
}

// Test-observable handle for the boot-time engine.start() — tests can
// `await bootApp.pendingSyncStart` to deterministically observe its
// completion (success or auth-failure recovery). Production code never
// reads this.
bootApp.pendingSyncStart = null as Promise<void> | null;

function treeHasLegacyIds(tree: SyncedTree): boolean {
  for (const profile of tree.profiles) {
    if (!isValidPbId(profile.id)) return true;
  }
  for (const routine of tree.routines) {
    if (!isValidPbId(routine.id)) return true;
    for (const task of routine.tasks) {
      if (!isValidPbId(task.id)) return true;
    }
  }
  return false;
}
